import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Switch, Select, Card, BodyText, Heading, Skeleton, TableScroll, Input } from '@aaimplatform/ui';
import type { InputProps } from '@aaimplatform/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';
import type { components as AssetClassificationComponents } from '@aaimplatform/institutional-types/generated/asset-classification';
import assetClassificationSchemas from '@aaimplatform/institutional-types/generated/asset-classification.schemas.json';
import { PercentInput } from './PercentInput';
import { pctText } from './percent';
import { parseWholeNumber, useFieldValidity, wholeNumberError } from './ChaThresholdsTab';

type AssetClassPolicy = components['schemas']['AssetClassPolicy'];
type RevaluationFrequency = components['schemas']['RevaluationFrequency'];
type AssetClassGroup = AssetClassificationComponents['schemas']['AssetClassGroup'];

interface AssetClassPoliciesTabProps {
  policies: AssetClassPolicy[];
  onChange: (policies: AssetClassPolicy[]) => void;
  /**
   * True when the platform asset-class flags could not be read, so an empty
   * list does not mean no class is available (r3 fi-g7-4).
   */
  availabilityUnreadable?: boolean;
  /**
   * True while the platform asset-class flags are still loading, so an empty
   * list is a read in progress and shows a loading state, not an empty one.
   */
  availabilityLoading?: boolean;
  /**
   * Called with false while a maximum price age field holds an entry that is
   * not a whole number of hours of at least 1, and with true once it does or
   * is emptied. The page holds Save while it reads false.
   */
  onValidityChange?: (valid: boolean) => void;
}

/**
 * Aaim's maximum price age when a class sets none, as the contract states it
 * (collateral-policy 1.5.0 AssetClassPolicy.maxPriceAgeHours: "Null or absent:
 * Aaim's default applies, which is 168 hours"; GH #4411). The valuation cycle
 * in lending-platform applies it; this page only says so.
 */
export const AAIM_DEFAULT_MAX_PRICE_AGE_HOURS = 168;

/** The spec minimum for maxPriceAgeHours. */
const MIN_PRICE_AGE_HOURS = 1;

/**
 * What the maximum price age does, stated on the page as Robert ruled on
 * 2026-10-25 04:16Z (GH #4411, Q2 (i) and Q3 (a)): the default, its effect on a
 * class valued without market data, and a price with no recorded time.
 */
export const MAX_PRICE_AGE_NOTE =
  `Measured from each price's own time to the valuation, for a live price and a carried one. ` +
  `Not set, Aaim's default of ${AAIM_DEFAULT_MAX_PRICE_AGE_HOURS} hours (7 days) applies. ` +
  `A class valued without market data carries its last price, so its positions leave the LTV ` +
  `once that price passes this age. A price with no recorded time counts as past it.`;

interface OptionalHoursInputProps
  extends Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'type' | 'error' | 'min' | 'step'> {
  /** The stored hours, or undefined when the class sets none. */
  value: number | undefined;
  /** Called with a valid entry, or with undefined when the field is emptied. */
  onChange: (hours: number | undefined) => void;
  /** Called with false while the typed entry is not valid, and with true once it is or the field unmounts. */
  onValidityChange?: (valid: boolean) => void;
}

/**
 * A whole-number hours input over an optional policy field. An emptied field
 * removes the value, so Aaim's default applies; an entry that is not a whole
 * number of at least 1 keeps its text with an inline error, never reaches the
 * policy, and holds Save through onValidityChange.
 */
function OptionalHoursInput({ value, onChange, onValidityChange, ...inputProps }: OptionalHoursInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const invalid =
    draft !== null && draft.trim() !== '' && parseWholeNumber(draft, MIN_PRICE_AGE_HOURS) === undefined;

  const validityRef = useRef(onValidityChange);
  validityRef.current = onValidityChange;
  useEffect(() => {
    validityRef.current?.(!invalid);
  }, [invalid]);
  useEffect(() => () => validityRef.current?.(true), []);

  return (
    <Input
      {...inputProps}
      type="number"
      min={MIN_PRICE_AGE_HOURS}
      step={1}
      value={draft ?? (value === undefined || value === null ? '' : String(value))}
      error={invalid ? wholeNumberError('hours', MIN_PRICE_AGE_HOURS) : undefined}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        if (raw.trim() === '') {
          onChange(undefined);
          return;
        }
        const parsed = parseWholeNumber(raw, MIN_PRICE_AGE_HOURS);
        if (parsed !== undefined) onChange(parsed);
      }}
      onBlur={() => {
        if (!invalid) setDraft(null);
      }}
    />
  );
}

// Each class's group (level 1 of Aaim's collateral taxonomy) is declared once,
// in x-asset-class-group on AssetClass in asset-classification.openapi.yaml
// (platform-model-proof R1c2, 2026-10-01), and read here from the generated
// schema. The hand-written map this replaces carried 15 of the 20 classes.
const ASSET_CLASS_SCHEMAS = assetClassificationSchemas.components.schemas;
const GROUP_OF_CLASS: Record<string, string> = ASSET_CLASS_SCHEMAS.AssetClass['x-asset-class-group'];

/** Display label per AssetClassGroup; exhaustive over the generated union. */
const GROUP_LABELS: Record<AssetClassGroup, string> = {
  EQUITIES: 'Equities',
  FIXED_INCOME: 'Fixed Income',
  REAL_ESTATE: 'Real Estate',
  CASH_AND_EQUIVALENTS: 'Cash and Equivalents',
  ALTERNATIVES: 'Alternatives',
  DIGITAL_ASSETS: 'Digital Assets',
  DERIVATIVES: 'Derivatives',
  UNCLASSIFIED: 'Unclassified',
};

function isAssetClassGroup(value: string): value is AssetClassGroup {
  return ASSET_CLASS_SCHEMAS.AssetClassGroup.enum.includes(value);
}

/** The group label for a class key, or undefined for a key the spec does not carry. */
export function assetClassGroupLabel(classKey: string): string | undefined {
  const group = GROUP_OF_CLASS[classKey];
  return group !== undefined && isAssetClassGroup(group) ? GROUP_LABELS[group] : undefined;
}

/** The group tag for a stored key that is not an AssetClass member. */
export const UNMAPPED_CATEGORY_LABEL = 'Unmapped';

/** Exhaustive over the generated RevaluationFrequency union. */
const REVALUATION_LABELS: Record<RevaluationFrequency, string> = {
  REAL_TIME: 'Real-time',
  HOURLY: 'Hourly',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
};

const REVALUATION_OPTIONS = (Object.keys(REVALUATION_LABELS) as RevaluationFrequency[]).map(
  (value) => ({ value, label: REVALUATION_LABELS[value] }),
);

/** Wording for an optional field the policy does not carry. */
export const NOT_SET = 'Not set';

/** The summary-row wording for a class's revaluation cadence. */
export function revaluationLabel(frequency: RevaluationFrequency | undefined): string {
  return frequency ? REVALUATION_LABELS[frequency] : NOT_SET;
}

/** The id of a row's detail panel, which its summary button controls. */
function detailId(taxonomyNodeId: string): string {
  return `asset-class-detail-${taxonomyNodeId}`;
}

/*
 * An OPTIONAL ratio field renders empty with a "Not set" placeholder when the
 * policy does not carry it, never as 0.0, and clearing it removes the field
 * rather than storing 0 (fi-g7-17). PercentInput's optional mode does both.
 */

/** The accepted-as-collateral state in words; the dot beside it is decoration (r3 fi-g7-8). */
export const ACCEPTED_LABEL = 'Accepted';
export const NOT_ACCEPTED_LABEL = 'Not accepted';

/**
 * The empty state when no asset class reaches the editors: the platform or
 * tenant flags hide every class, or the policy on the page carries none (a
 * policy that failed to load included, under the page's load alert).
 * Advance Rate Programs says the same words rather than sending the FI admin
 * here to enable a class this page cannot show (GH #677).
 */
export const NO_CLASSES_AVAILABLE = 'No asset classes are available to configure.';

/**
 * AssetClassPoliciesTab - Per-asset-class policy configuration
 *
 * Each row is keyed to a ReferenceModel taxonomy node. FIs configure:
 * - Enabled/disabled (whether they accept this class)
 * - Max LTV cap
 * - Discount rate (haircut)
 * - Volatility buffer and threshold
 * - Revaluation frequency
 * - Maximum price age (GH #4411)
 * - Minimum liquidity score
 */
export function AssetClassPoliciesTab({
  policies,
  onChange,
  availabilityUnreadable = false,
  availabilityLoading = false,
  onValidityChange,
}: AssetClassPoliciesTabProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const reportValidity = useFieldValidity(onValidityChange);

  const handleToggle = (taxonomyNodeId: string) => {
    setExpandedRow(prev => prev === taxonomyNodeId ? null : taxonomyNodeId);
  };

  const handleFieldChange = (
    index: number,
    field: keyof AssetClassPolicy,
    value: unknown,
  ) => {
    const updated = policies.map((p, i) =>
      i === index ? { ...p, [field]: value } : p,
    );
    onChange(updated);
  };

  /** Sets an optional field, or removes it when the value is undefined. */
  const handleOptionalChange = (
    index: number,
    field: 'minLiquidityScore' | 'volatilityBuffer' | 'volatilityThreshold' | 'revaluationFrequency' | 'maxPriceAgeHours',
    value: number | RevaluationFrequency | undefined,
  ) => {
    const updated = policies.map((p, i) => {
      if (i !== index) return p;
      const next: AssetClassPolicy = { ...p };
      if (value === undefined) {
        delete next[field];
      } else {
        (next as Record<typeof field, unknown>)[field] = value;
      }
      return next;
    });
    onChange(updated);
  };

  return (
    <div className="space-y-6">
      {/* Page title lives in the sticky topnav (AppHeader); no redundant H1.
          Reset and Save sit in one row on CollateralPolicyPage (r3 fi-g7-11). */}
    <Card>
      {/* Horizontal scroll on narrow viewports keeps the 12-col emulated
          table aligned instead of collapsing into unreadable slivers. The
          region is a size container so an expanded detail panel can pin
          itself to the visible width (100cqw) while the rows scroll. */}
      <TableScroll label="Asset class policies" className="[container-type:inline-size]">
      {/* Table header */}
      <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-recessed text-label-xs font-semibold uppercase text-tertiary min-w-[720px]">
        <div className="col-span-3">Asset Type</div>
        <div className="col-span-2">Accepted</div>
        <div className="col-span-2 text-right">Max LTV</div>
        <div className="col-span-1 text-right">Discount</div>
        <div className="col-span-2 text-right">Vol Buffer</div>
        <div className="col-span-2 text-right">Revaluation</div>
      </div>

      {policies.map((policy, index) => {
        const isExpanded = expandedRow === policy.taxonomyNodeId;
        const hasExpanded = expandedRow !== null;
        const isReceded = hasExpanded && !isExpanded;
        // A stored key that is not an AssetClass member shows as Unmapped with
        // its code in the title, never as a blank tag (fi-g7-2): three baseline
        // ids drifted from the canonical AssetClass ids and rendered as nothing
        // (GH #2841).
        const groupLabel = assetClassGroupLabel(policy.taxonomyNodeId);
        return (
          <div key={policy.taxonomyNodeId} className={`min-w-[720px] transition-opacity ${isReceded ? 'opacity-50' : ''}`}>
            {/* Summary row. A class the institution does not accept keeps its
                values at full contrast; the Accepted column says so in words
                instead of the row fading to 40 percent (r3 fi-g7-8). */}
            <button
              type="button"
              onClick={() => handleToggle(policy.taxonomyNodeId)}
              aria-expanded={isExpanded}
              aria-controls={detailId(policy.taxonomyNodeId)}
              className={`w-full grid grid-cols-12 gap-4 items-center px-4 py-2.5 text-left border-b border-recessed hover:bg-elevated transition-colors min-w-[720px] ${isExpanded ? 'bg-elevated font-medium' : ''}`}
            >
              <div className="col-span-3 flex items-center gap-2">
                {/* shrink-0: a wrapped two-line name shrank the chevron (r3 fi-g7-17). */}
                {isExpanded
                  ? <ChevronDown className="w-3.5 h-3.5 shrink-0 text-tertiary" aria-hidden="true" />
                  : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-tertiary" aria-hidden="true" />
                }
                <span className="text-sm font-medium text-primary">
                  {policy.displayName}
                </span>
                {groupLabel ? (
                  <span className="text-xs font-sans text-tertiary">
                    {groupLabel}
                  </span>
                ) : (
                  <span
                    className="text-xs font-sans text-tertiary italic"
                    title={policy.taxonomyNodeId}
                  >
                    {UNMAPPED_CATEGORY_LABEL}
                  </span>
                )}
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className={`inline-block w-2 h-2 shrink-0 rounded-full ${policy.enabled ? 'bg-growth' : 'bg-recessed'}`}
                />
                <span className={`text-sm font-sans ${policy.enabled ? 'text-primary' : 'text-tertiary'}`}>
                  {policy.enabled ? ACCEPTED_LABEL : NOT_ACCEPTED_LABEL}
                </span>
              </div>
              <div className="col-span-2 text-right font-mono text-sm text-primary">
                {pctText(policy.maxLtv)}%
              </div>
              <div className="col-span-1 text-right font-mono text-sm text-primary">
                {pctText(policy.discountRate)}%
              </div>
              <div className="col-span-2 text-right font-mono text-sm text-primary">
                {policy.volatilityBuffer != null ? `${pctText(policy.volatilityBuffer)}%` : (
                  <span className="font-sans text-tertiary">{NOT_SET}</span>
                )}
              </div>
              <div className="col-span-2 text-right text-sm text-secondary">
                {revaluationLabel(policy.revaluationFrequency)}
              </div>
            </button>

            {/* Expanded detail. sticky left-0 plus the region's width (100cqw)
                keep the form in view when the rows are scrolled sideways; as
                a plain block it took the region's width at the row's scroll
                offset and its left column sat off-screen (GH #1668 css-06). */}
            {isExpanded && (
              <div
                id={detailId(policy.taxonomyNodeId)}
                className="sticky left-0 w-[100cqw] border-t border-recessed border-b-2 border-b-secondary/30 bg-surface px-4 py-5 space-y-6 shadow-sm"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {/* Left column: Core parameters */}
                  <div className="space-y-5">
                    {/* Semantic level 2: the page h1 is the AppHeader title and
                        no heading sits between it and this panel. as={4} keeps
                        the sans face the level-4 heading had. */}
                    <Heading level={2} as={4} size="compact" className="mb-4">Core Parameters</Heading>

                    <div className="flex items-center justify-between">
                      <BodyText variant="ui" size="sm">Accept as collateral</BodyText>
                      <Switch
                        aria-label={`Accept ${policy.displayName} as collateral`}
                        isSelected={policy.enabled}
                        onChange={(isSelected) => handleFieldChange(index, 'enabled', isSelected)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <BodyText variant="ui" size="sm">Maximum LTV (%)</BodyText>
                        <BodyText variant="ui" size="xs" color="tertiary">Ceiling for this asset class</BodyText>
                      </div>
                      <div className="w-24 shrink-0">
                        <PercentInput
                          aria-label="Maximum LTV (%)"
                          value={policy.maxLtv}
                          onChange={(ratio) => { if (ratio !== undefined) handleFieldChange(index, 'maxLtv', ratio); }}
                          disabled={!policy.enabled}
                          className="text-right font-mono"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <BodyText variant="ui" size="sm">Discount rate (%)</BodyText>
                        <BodyText variant="ui" size="xs" color="tertiary">Haircut applied to market value</BodyText>
                      </div>
                      <div className="w-24 shrink-0">
                        <PercentInput
                          aria-label="Discount rate (%)"
                          value={policy.discountRate}
                          onChange={(ratio) => { if (ratio !== undefined) handleFieldChange(index, 'discountRate', ratio); }}
                          disabled={!policy.enabled}
                          className="text-right font-mono"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <BodyText variant="ui" size="sm">Min liquidity score</BodyText>
                        <BodyText variant="ui" size="xs" color="tertiary">0 = illiquid, 100 = highly liquid</BodyText>
                      </div>
                      <div className="w-24 shrink-0">
                        <PercentInput
                          optional
                          aria-label="Min liquidity score"
                          value={policy.minLiquidityScore}
                          placeholder={NOT_SET}
                          onChange={(ratio) => handleOptionalChange(index, 'minLiquidityScore', ratio)}
                          disabled={!policy.enabled}
                          className="text-right font-mono"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <BodyText variant="ui" size="sm">Revaluation frequency</BodyText>
                      {/* The Select's own wrapper is full width, so the size
                          lives on this div; a width class passed to Select
                          sizes only the <select> and strands the chevron. An
                          absent cadence selects the "Not set" placeholder
                          rather than showing Daily as though it were set. */}
                      <div className="w-36 shrink-0">
                        <Select
                          id={`revaluation-${policy.taxonomyNodeId}`}
                          value={policy.revaluationFrequency ?? ''}
                          placeholder={NOT_SET}
                          onChange={(e) => handleOptionalChange(index, 'revaluationFrequency', e.target.value as RevaluationFrequency)}
                          options={REVALUATION_OPTIONS}
                          disabled={!policy.enabled}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <div>
                          <BodyText variant="ui" size="sm">Maximum price age (hours)</BodyText>
                          <BodyText variant="ui" size="xs" color="tertiary">An older price leaves the position out of the LTV</BodyText>
                        </div>
                        <div className="w-24 shrink-0">
                          <OptionalHoursInput
                            aria-label="Maximum price age (hours)"
                            value={policy.maxPriceAgeHours ?? undefined}
                            placeholder={String(AAIM_DEFAULT_MAX_PRICE_AGE_HOURS)}
                            onChange={(hours) => handleOptionalChange(index, 'maxPriceAgeHours', hours)}
                            onValidityChange={(valid) => reportValidity(`maxPriceAgeHours-${policy.taxonomyNodeId}`, valid)}
                            disabled={!policy.enabled}
                            className="text-right font-mono"
                          />
                        </div>
                      </div>
                      <BodyText variant="ui" size="xs" color="tertiary" className="mt-2">
                        {MAX_PRICE_AGE_NOTE}
                      </BodyText>
                    </div>
                  </div>

                  {/* Right column: Volatility */}
                  <div className="space-y-5">
                    <Heading level={2} as={4} size="compact" className="mb-4">Volatility Controls</Heading>

                    <div className="flex items-center justify-between">
                      <div>
                        <BodyText variant="ui" size="sm">Volatility buffer (%)</BodyText>
                        <BodyText variant="ui" size="xs" color="tertiary">Extra discount when vol exceeds threshold</BodyText>
                      </div>
                      <div className="w-24 shrink-0">
                        <PercentInput
                          optional
                          aria-label="Volatility buffer (%)"
                          value={policy.volatilityBuffer}
                          placeholder={NOT_SET}
                          onChange={(ratio) => handleOptionalChange(index, 'volatilityBuffer', ratio)}
                          disabled={!policy.enabled}
                          className="text-right font-mono"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <BodyText variant="ui" size="sm">Volatility threshold (%)</BodyText>
                        <BodyText variant="ui" size="xs" color="tertiary">30-day annualized vol that triggers buffer</BodyText>
                      </div>
                      <div className="w-24 shrink-0">
                        <PercentInput
                          optional
                          aria-label="Volatility threshold (%)"
                          value={policy.volatilityThreshold}
                          placeholder={NOT_SET}
                          onChange={(ratio) => handleOptionalChange(index, 'volatilityThreshold', ratio)}
                          disabled={!policy.enabled}
                          className="text-right font-mono"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* CHA tier thresholds, action windows and performance
                    adjustment are edited in one place, Advance Rate Programs.
                    This row carried a second editor for the same fields under
                    different labels and a collapsed tier palette (fi-g7-5). */}
                <div className="border-t border-recessed pt-5">
                  <BodyText variant="ui" size="sm" color="secondary">
                    CHA tier thresholds, action windows and performance adjustment for this class are set under{' '}
                    <Link to="/policy/cha-thresholds" className="text-accent hover:underline">
                      Advance Rate Programs
                    </Link>
                    .
                  </BodyText>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {policies.length === 0 && availabilityLoading && (
        <div role="status" className="px-4 py-4 space-y-3">
          <span className="sr-only">Loading asset classes</span>
          <Skeleton height={40} className="w-full" />
          <Skeleton height={40} className="w-full" />
          <Skeleton height={40} className="w-full" />
        </div>
      )}
      {policies.length === 0 && !availabilityLoading && (
        <div className="px-4 py-12 text-center min-w-[720px]">
          <BodyText variant="ui" color="tertiary">
            {availabilityUnreadable
              ? 'Asset-class availability could not be read, so no classes are shown.'
              : NO_CLASSES_AVAILABLE}
          </BodyText>
        </div>
      )}
      </TableScroll>
    </Card>
    </div>
  );
}
